import { 
  doc, 
  collection,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { db, auth } from './config';

/**
 * Save a site draft to Firestore
 * 
 * @param {Object} draftData - Site data to save as draft
 * @param {string|null} draftId - Optional draft ID for updating existing draft
 * @returns {Promise<Object>} Result with success status and data
 */
export const saveDraft = async (draftData, draftId = null) => {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      return { success: false, error: 'User must be logged in to save drafts' };
    }
    
    const userId = auth.currentUser.uid;
    const draftsCollection = collection(db, 'siteDrafts');
    
    // Prepare data
    const draftObject = {
      userId,
      siteData: draftData,
      updatedAt: serverTimestamp()
    };
    
    let result;
    
    // Update existing or create new draft
    if (draftId) {
      const draftRef = doc(db, 'siteDrafts', draftId);
      await updateDoc(draftRef, draftObject);
      result = { id: draftId };
    } else {
      // Add created timestamp for new drafts
      draftObject.createdAt = serverTimestamp();
      const docRef = await addDoc(draftsCollection, draftObject);
      result = { id: docRef.id };
    }
    
    return { success: true, data: result };
  } catch (error) {
    console.error('Error saving draft:', error);
    return { success: false, error: error.message || 'Failed to save draft' };
  }
};

/**
 * Get all drafts for the current user
 * 
 * @returns {Promise<Object>} Result with success status and data
 */
export const getDrafts = async () => {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      return { success: false, error: 'User must be logged in to access drafts' };
    }
    
    const userId = auth.currentUser.uid;
    const draftsCollection = collection(db, 'siteDrafts');
    
    // Query drafts for this user, ordered by last updated
    const draftsQuery = query(
      draftsCollection,
      where('userId', '==', userId),
      orderBy('updatedAt', 'desc'),
      limit(20) // Limit to 20 drafts to prevent excessive reads
    );
    
    const querySnapshot = await getDocs(draftsQuery);
    const drafts = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Convert timestamps to dates for easier handling in UI
      const updatedAt = data.updatedAt?.toDate().toISOString() || new Date().toISOString();
      const createdAt = data.createdAt?.toDate().toISOString() || updatedAt;
      
      drafts.push({
        id: doc.id,
        siteData: data.siteData,
        updatedAt,
        createdAt
      });
    });
    
    return { success: true, data: drafts };
  } catch (error) {
    console.error('Error getting drafts:', error);
    return { success: false, error: error.message || 'Failed to get drafts' };
  }
};

/**
 * Delete a draft
 * 
 * @param {string} draftId - ID of the draft to delete
 * @returns {Promise<Object>} Result with success status
 */
export const deleteDraft = async (draftId) => {
  try {
    // Ensure user is authenticated
    if (!auth.currentUser) {
      return { success: false, error: 'User must be logged in to delete drafts' };
    }
    
    // Get the draft to verify ownership
    const draftRef = doc(db, 'siteDrafts', draftId);
    const draftSnap = await getDoc(draftRef);
    
    if (!draftSnap.exists()) {
      return { success: false, error: 'Draft not found' };
    }
    
    // Verify ownership
    const draftData = draftSnap.data();
    if (draftData.userId !== auth.currentUser.uid) {
      return { success: false, error: 'Not authorized to delete this draft' };
    }
    
    // Delete the draft
    await deleteDoc(draftRef);
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting draft:', error);
    return { success: false, error: error.message || 'Failed to delete draft' };
  }
};
